import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';

const AuthContext = createContext();

const INATIVIDADE_MS  = 30 * 60 * 1000;  // 30 min
const THROTTLE_MS     = 30 * 1000;        // grava localStorage no máx a cada 30s
const CHAVE_ATIVIDADE = 'ultima_atividade';
const EVENTOS_ATIVIDADE = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

export function AuthProvider({ children }) {
  const [usuario, setUsuario] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [quotaIa, setQuotaIa] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const ultimaEscritaRef = useRef(0);

  async function carregarTenant() {
    try {
      const { data } = await api.get('/tenant/meu-tenant');
      console.log('Tenant carregado:', data);
      setTenant(data);
    } catch (err) {
      console.error('Erro ao carregar tenant:', err.response?.status, err.response?.data);
    }
  }

  async function carregarQuotaIa() {
    try {
      const { data } = await api.get('/ia/quota');
      setQuotaIa(data);
    } catch {
      // Falha na API → sem dados de quota; trata como sem acesso para não liberar indevidamente
      setQuotaIa({ limite: 0, ilimitado: false, creditosIA: 0 });
    }
  }

  // Redireciona para /login com flag de sessão expirada
  const expirarSessao = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('usuario');
    localStorage.removeItem(CHAVE_ATIVIDADE);
    setUsuario(null);
    setTenant(null);
    setQuotaIa(null);
    window.location.href = '/login?sessao=expirada';
  }, []);

  // Checa inatividade; retorna true se a sessão expirou e foi encerrada
  const verificarInatividade = useCallback(() => {
    const ultima = parseInt(localStorage.getItem(CHAVE_ATIVIDADE) || '0', 10);
    if (ultima && Date.now() - ultima > INATIVIDADE_MS) {
      expirarSessao();
      return true;
    }
    return false;
  }, [expirarSessao]);

  // Carregamento inicial — também verifica se aba foi reaberta após expiração
  useEffect(() => {
    const token        = localStorage.getItem('token');
    const usuarioSalvo = localStorage.getItem('usuario');
    if (token && usuarioSalvo) {
      if (verificarInatividade()) return; // expirou enquanto aba estava fechada
      try {
        setUsuario(JSON.parse(usuarioSalvo));
      } catch {
        localStorage.removeItem('usuario');
        localStorage.removeItem('token');
        setCarregando(false);
        return;
      }
      carregarTenant();
      carregarQuotaIa();
    }
    setCarregando(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Rastreamento de inatividade — ativo só quando logado
  useEffect(() => {
    if (!usuario) return;

    const registrarAtividade = () => {
      const agora = Date.now();
      if (agora - ultimaEscritaRef.current > THROTTLE_MS) {
        localStorage.setItem(CHAVE_ATIVIDADE, String(agora));
        ultimaEscritaRef.current = agora;
      }
    };

    EVENTOS_ATIVIDADE.forEach(ev =>
      window.addEventListener(ev, registrarAtividade, { passive: true })
    );

    // Checa a cada 30s + quando a aba volta ao foco
    const intervalo = setInterval(verificarInatividade, 30_000);
    window.addEventListener('focus', verificarInatividade);

    return () => {
      EVENTOS_ATIVIDADE.forEach(ev =>
        window.removeEventListener(ev, registrarAtividade)
      );
      clearInterval(intervalo);
      window.removeEventListener('focus', verificarInatividade);
    };
  }, [usuario, verificarInatividade]);

  async function login(email, senha) {
    const { data } = await api.post('/auth/login', { email, senha });
    localStorage.setItem('token', data.token);
    localStorage.setItem('usuario', JSON.stringify(data.usuario));
    localStorage.setItem(CHAVE_ATIVIDADE, String(Date.now())); // inicia o timer
    setUsuario(data.usuario);
    carregarTenant().catch(() => {});
    carregarQuotaIa().catch(() => {});
    return data;
  }

  function logout() {
    localStorage.removeItem('token');
    localStorage.removeItem('usuario');
    localStorage.removeItem(CHAVE_ATIVIDADE);
    setUsuario(null);
    setTenant(null);
    setQuotaIa(null);
  }

  // true quando o plano ou créditos avulsos permitem usar a IA
  // null = ainda carregando → não bloqueia (evita flash de lock na inicialização)
  const temAcessoIA = quotaIa === null
    || quotaIa.ilimitado
    || quotaIa.limite > 0
    || quotaIa.creditosIA > 0;

  return (
    <AuthContext.Provider value={{
      usuario,
      tenant,
      quotaIa,
      temAcessoIA,
      login,
      logout,
      carregando,
      carregarTenant,
      carregarQuotaIa,
      isSuperadmin: usuario?.role === 'superadmin',
      addonEmail: tenant?.addon_email || false,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
